import { useTranslation } from 'react-i18next';

import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { CollectionGuide, TopicLinks, useCollectionCopy } from '@todo/web-ui/components/collection-guide';
import { EmptyState } from '@todo/web-ui/components/empty-state';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { TourGrid } from '@todo/web-ui/components/tour-grid';
import { isCollectionIndexable } from '@todo/web-ui/lib/collections';
import { SEE_ALSO_PAGES } from '@/lib/collections';
import { YACHT_TOURS_QUERY, YACHT_TOURS_SEED_KEY } from '@/lib/prefetch';
import { useTours } from '@todo/core/hooks/use-tours';
import { useLocale } from '@todo/core/i18n/use-locale';
import { boatsCrumb, breadcrumbJsonLd, faqPageJsonLd, seoTitle } from '@todo/core/seo';

// Yacht and boat trips in Dubai — the yacht_charter tours (Click&Boat's boat
// trips and Viator's yacht tours), best ranked first as tours_by_theme orders
// them. Each card links to its /tours/[id] page. Laid out like a category
// collection ([collection].tsx): SEO <title>, keyword <h1>, and under the
// cards the guide + FAQ (FAQPage JSON-LD) from `collections.boats` in the
// resources. Indexable by a collection's rule (isCollectionIndexable): its
// guide and at least one tour — first in the near-copy comparison, so never a
// copy itself.
export default function BoatsIndex() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { tours, loading } = useTours(YACHT_TOURS_QUERY, YACHT_TOURS_SEED_KEY);
  const copy = useCollectionCopy('boats');

  return (
    <Page>
      <PageHead
        title={seoTitle(t('collections.boats.seoTitle'))}
        description={t('collections.boats.meta')}
        path="boats"
        indexable={isCollectionIndexable(copy.guide.length > 0, tours.length, undefined)}>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(locale, [{ name: boatsCrumb(locale).name }]))}</script>
        {copy.faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script> : null}
        {seedScript(YACHT_TOURS_SEED_KEY)}
      </PageHead>

      <Section id="boats-title" level={1} title={t('collections.boats.heading')}>
        <Intro>{t('boats.intro')}</Intro>
        <TopicLinks collections={[]} pages={SEE_ALSO_PAGES.boats} />
        {tours.length ? (
          <TourGrid tours={tours} />
        ) : loading ? (
          <CardGrid>
            <CardSkeletons />
          </CardGrid>
        ) : (
          <EmptyState exclude="boats" />
        )}
      </Section>

      <CollectionGuide slug="boats" copy={copy} />
    </Page>
  );
}
